import { Alert, Button, Card, Flex, Group, Stack, Text, Badge } from "@mantine/core";
import { IconAlertTriangle, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { SimilarIdea } from "../../hooks/useCheckSimilarIdeas";

interface SimilarIdeasModalProperties {
  similarIdeas: SimilarIdea[];
  onConfirm: () => void;
  onCancel: () => void;
  previewMode?: boolean;
}

/**
 * Modal que exibe ideias similares encontradas e permite ao usuário
 * decidir se deseja continuar ou cancelar a criação da ideia.
 * Agora usa props diretas em vez de ContextModalProps para evitar re-renders.
 * @param root0
 * @param root0.similarIdeas
 * @param root0.onConfirm
 * @param root0.onCancel
 * @param root0.previewMode
 */
export function SimilarIdeasModal({
  similarIdeas,
  onConfirm,
  onCancel,
  previewMode = false
}: SimilarIdeasModalProperties) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((ideaId: string) => {
    setExpandedIds((previous) => {
      const newSet = new Set(previous);
      if (newSet.has(ideaId)) {
        newSet.delete(ideaId);
      } else {
        newSet.add(ideaId);
      }
      return newSet;
    });
  }, []);

  const handleToggleExpanded = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const ideaId = event.currentTarget.dataset.ideaId;
      if (!ideaId) {
        return;
      }
      toggleExpanded(ideaId);
    },
    [toggleExpanded]
  );

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
  };

  const formatSimilarity = (similarity: number) => {
    return `${Math.round(similarity * 100)}%`;
  };

  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 0.9) return "red";
    if (similarity >= 0.8) return "orange";
    return "yellow";
  };

  return (
    <Stack gap="md">
      <Alert icon={<IconAlertTriangle size={20} />} color={previewMode ? "blue" : "yellow"}>
        <Text size="sm">
          {previewMode
            ? `Encontramos ${similarIdeas.length} ${similarIdeas.length === 1 ? "ideia similar" : "ideias similares"} já cadastradas. Revise antes de continuar com o cadastro.`
            : `Encontramos ${similarIdeas.length} ${similarIdeas.length === 1 ? "ideia similar" : "ideias similares"} já cadastradas. Verifique se não é uma duplicata antes de continuar.`}
        </Text>
      </Alert>

      <Stack gap="xs">
        {similarIdeas.map((idea) => {
          const isExpanded = expandedIds.has(idea.id);
          return (
            <Card key={idea.id} shadow="sm" padding="md" withBorder>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={600} size="sm" style={{ flex: 1 }}>
                    {idea.titulo}
                  </Text>
                  <Badge color={getSimilarityColor(idea.similarity)} variant="light">
                    {formatSimilarity(idea.similarity)} similar
                  </Badge>
                </Group>

                <Text size="sm" c="dimmed" lineClamp={isExpanded ? undefined : 2} style={{ whiteSpace: "pre-wrap" }}>
                  {idea.descricao}
                </Text>

                <Group justify="flex-end">
                  <Button
                    variant="subtle"
                    size="xs"
                    data-idea-id={idea.id}
                    onClick={handleToggleExpanded}
                    rightSection={isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                  >
                    {isExpanded ? "Ver menos" : "Ver mais"}
                  </Button>
                </Group>
              </Stack>
            </Card>
          );
        })}
      </Stack>

      <Flex justify="flex-end" gap="sm" mt="md">
        {previewMode ? (
          <Button onClick={handleCancel}>Fechar</Button>
        ) : (
          <>
            <Button variant="default" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button onClick={handleConfirm}>Continuar Mesmo Assim</Button>
          </>
        )}
      </Flex>
    </Stack>
  );
}

export default SimilarIdeasModal;
